// src/lib/posthog/identity.ts
//
// Pure identity helpers for the PostHog instrumentation. No db, no `window`,
// no env read at module load: every input (cookie header, project key,
// clock) is passed in by the caller.

/**
 * A person's `$set` properties. Deliberately built field-by-field
 * (never spread from an arbitrary input object) so an email, phone or other
 * PII field on the source object can never leak through: only the six named
 * properties below are ever assigned.
 */
export interface PersonSet {
  user_type?: "owner" | "staff";
  merchant_id?: string;
  country?: string;
  email_verified?: boolean;
  kyc_status?: string;
  onboarding_status?: string;
  is_registered: true;
  /**
   * `$set_once` only — never written by `personSetFromMeta`
   * below. Built by `personSetOnceFromMeta` instead, so a `$set_once` value
   * can never accidentally travel through the `$set` call and be
   * overwritten on a later `identify`.
   */
  signup_method?: "email";
}

/**
 * The subset of `/api/user/meta` that feeds `personSetFromMeta`.
 * Optional/nullable because not every caller has every field yet.
 */
export interface PersonMeta {
  userType?: string | null;
  merchantId?: string | null;
  country?: string | null;
  emailVerified?: boolean | null;
  kycStatus?: string | null;
  onboardingStatus?: string | null;
  /** `/api/user/meta`'s `signupMethod`: `"email"` or `null`. */
  signupMethod?: string | null;
}

/**
 * Exported so every writer of a `$set` string property (`personSetFromMeta`
 * below, and `lifecycle.ts`'s `syncPersonStatus`) shares the exact same
 * "blank means absent" rule — two helpers for the same property that
 * disagreed on an empty string would silently corrupt cohorts.
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * Builds the `$set` object for the `identify` call. Reads only the six
 * allowed fields off `meta` by name; any other property present on the input
 * (an email, a phone number, a hash) is never read and can never appear in
 * the result.
 */
export function personSetFromMeta(meta: PersonMeta): PersonSet {
  const set: PersonSet = { is_registered: true };

  if (meta.userType === "owner" || meta.userType === "staff") {
    set.user_type = meta.userType;
  }
  if (isNonEmptyString(meta.merchantId)) set.merchant_id = meta.merchantId;
  if (isNonEmptyString(meta.country)) set.country = meta.country;
  if (typeof meta.emailVerified === "boolean") {
    set.email_verified = meta.emailVerified;
  }
  if (isNonEmptyString(meta.kycStatus)) set.kyc_status = meta.kycStatus;
  if (isNonEmptyString(meta.onboardingStatus)) {
    set.onboarding_status = meta.onboardingStatus;
  }

  return set;
}

/**
 * Builds the `$set_once` object for the `identify` call: today
 * that is `signup_method` alone — `first_touch_*` is already written once by
 * `startPostHog()` (src/lib/posthog/client.ts) and must never be duplicated
 * here. Only the exact literal `"email"` is ever passed
 * through; any other value — `null` (a bridge or Trapyfy-sourced account,
 * `/api/user/meta` cannot source it) or anything unrecognised — is omitted,
 * so a value we cannot vouch for is never written, first or otherwise.
 */
export function personSetOnceFromMeta(meta: PersonMeta): Pick<PersonSet, "signup_method"> {
  const setOnce: Pick<PersonSet, "signup_method"> = {};

  if (meta.signupMethod === "email") {
    setOnce.signup_method = "email";
  }

  return setOnce;
}

/**
 * `(now - since) / 3600`, rounded to one decimal (`hours_since_signup`),
 * round-half-up. `now` is always injected by the caller for determinism.
 */
export function hoursSince(since: Date, now: Date): number {
  const hours = (now.getTime() - since.getTime()) / 3_600_000;
  return Math.round(hours * 10) / 10;
}

/**
 * Whole days elapsed since `since`, floored (the `days_since_signup` of
 * `activated` and `onboarding_completed`): a
 * merchant who signed up 35.75 days ago has not yet completed a 36th day, so
 * this reports 35. `now` is always injected by the caller for determinism; a
 * `now` that precedes `since` (clock skew) returns a negative count rather
 * than throwing.
 */
export function daysSince(since: Date, now: Date): number {
  const days = (now.getTime() - since.getTime()) / 86_400_000;
  return Math.floor(days);
}

// posthog-js names its cookie `ph_<token>_posthog`, where `token` is the
// project key with `+`, `/` and `=` swapped for `PL`, `SL` and `EQ` so the
// key can never break a cookie-attribute pair. Mirrored here so we look up
// the exact cookie posthog-js wrote.
// Source: node_modules/posthog-js/lib/src/posthog-persistence.js `parseName`
// (posthog-js 1.424.0): `'ph_' + token.replace(/\+/g,'PL').replace(/\//g,'SL')
// .replace(/=/g,'EQ') + '_posthog'`; `distinct_id` is `constants.DISTINCT_ID`
// in the same package's `constants.js`.
function cookieNameForKey(key: string): string {
  const token = key.replace(/\+/g, "PL").replace(/\//g, "SL").replace(/=/g, "EQ");
  return `ph_${token}_posthog`;
}

/** Splits a raw `Cookie` request header into a name → raw-value map. */
function parseCookieHeader(header: string): Map<string, string> {
  const jar = new Map<string, string>();
  for (const pair of header.split(";")) {
    const eq = pair.indexOf("=");
    if (eq === -1) continue;
    const name = pair.slice(0, eq).trim();
    const value = pair.slice(eq + 1).trim();
    if (name) jar.set(name, value);
  }
  return jar;
}

/** Plausible id: a non-empty, trimmed string within a sane length. */
function isPlausibleDistinctId(value: unknown): value is string {
  return isNonEmptyString(value) && value.trim().length <= 200;
}

/**
 * Reads the anonymous `distinct_id` out of the browser's
 * `ph_<key>_posthog` cookie. `cookieHeader` is the raw `Cookie` request
 * header; `projectKey` is `NEXT_PUBLIC_POSTHOG_KEY` (passed in — this module
 * never reads env itself). Only that project's cookie is read: a cookie
 * written by another PostHog project on the same registrable domain is
 * ignored, so a user is never aliased to a foreign anonymous id.
 *
 * Never throws. Returns `null` when the header or the key is absent, the
 * cookie is missing, or its value is not the plausible-id shape.
 */
export function parseAnonymousDistinctId(
  cookieHeader: string | null | undefined,
  projectKey: string | null | undefined,
): string | null {
  try {
    if (!isNonEmptyString(cookieHeader) || !isNonEmptyString(projectKey)) return null;

    const raw = parseCookieHeader(cookieHeader).get(cookieNameForKey(projectKey));
    if (raw === undefined) return null;

    const decoded = decodeURIComponent(raw);
    const parsed: unknown = JSON.parse(decoded);
    if (typeof parsed !== "object" || parsed === null) return null;

    const distinctId = (parsed as Record<string, unknown>)["distinct_id"];
    return isPlausibleDistinctId(distinctId) ? distinctId.trim() : null;
  } catch {
    return null;
  }
}
